import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { ExportButton } from 'components/shared/ExportButton';
import { useNotifications } from 'modules/notification';
import { SharedFinanceFilters } from 'modules/finance/sharedFilters';
import { DateRangeBanner } from 'modules/finance/ui/components/DateRangeBanner';
import { VOApiFilterType } from 'modules/vendorOpportunity/filters';
import { useFilterOptions } from 'modules/vendorOpportunity/list/api';
import {
  ClosedLostFiltersState,
  ClosedLostSortKey,
  ClosedLostSubmissionStage,
  FinanceInsurancePerformanceReportGroupBy,
} from '../../types';
import { useClosedLostReportData } from '../../hooks/useClosedLostReportData';
import { closedLostReportApi } from '../../api';
import { ClosedLostFilters, ClosedLostKpis, ClosedLostSavedViews, ClosedLostTable } from '../components';

interface Props {
  sharedFilters: SharedFinanceFilters;
  onSharedFiltersChange: (filters: SharedFinanceFilters) => void;
}

export const ClosedLostPerformance = ({ sharedFilters, onSharedFiltersChange }: Props) => {
  const { showNotification } = useNotifications();
  const [groupBy, setGroupBy] = useState<FinanceInsurancePerformanceReportGroupBy>(
    FinanceInsurancePerformanceReportGroupBy.SalesRep
  );
  const [submissionStage, setSubmissionStage] = useState<ClosedLostSubmissionStage>('ALL');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [sortKey, setSortKey] = useState<ClosedLostSortKey>('dealsLost');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [isExportInProgress, setIsExportInProgress] = useState(false);

  const { options: rawDepartmentOptions, loading: departmentOptionsLoading } = useFilterOptions(
    VOApiFilterType.BPF_DEPARTMENT
  );
  const departmentOptions = useMemo(
    () => [...rawDepartmentOptions].sort((a, b) => a.label.localeCompare(b.label)),
    [rawDepartmentOptions]
  );

  // No "All departments" option: loss reason taxonomy is BPF/department-scoped, so default to the
  // first department alphabetically once options load.
  useEffect(() => {
    if (!departmentId && departmentOptions.length > 0) {
      setDepartmentId(String(departmentOptions[0].value));
    }
  }, [departmentId, departmentOptions]);

  const filters = useMemo<ClosedLostFiltersState>(
    () => ({
      groupBy,
      submissionStage,
      departmentId,
      dateRange: sharedFilters.dateRange,
      customDateRange: sharedFilters.customDateRange,
      dateType: sharedFilters.dateType,
      paymentType: sharedFilters.paymentType,
    }),
    [groupBy, submissionStage, departmentId, sharedFilters]
  );

  const { rows, totals, reasonTotals, loading, input } = useClosedLostReportData(filters, {
    key: sortKey,
    value: sortDir,
  });

  const handleApplyView = (view: ClosedLostFiltersState) => {
    setGroupBy(view.groupBy);
    setSubmissionStage(view.submissionStage);
    setDepartmentId(view.departmentId);
    onSharedFiltersChange({
      ...sharedFilters,
      dateRange: view.dateRange,
      customDateRange: view.customDateRange,
      dateType: view.dateType,
      paymentType: view.paymentType,
    });
  };

  const handleSort = (key: ClosedLostSortKey) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const handleExport = async () => {
    setIsExportInProgress(true);
    try {
      await closedLostReportApi.exportClosedLostReport(input);
    } catch (error: unknown) {
      showNotification('Failed to export closed lost report CSV', { type: 'error' });
    } finally {
      setIsExportInProgress(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
        flexWrap="wrap"
        gap={1}
      >
        <ClosedLostFilters
          filters={filters}
          departmentOptions={departmentOptions}
          departmentOptionsLoading={departmentOptionsLoading}
          onChange={newFilters => {
            if (newFilters.groupBy !== groupBy) setGroupBy(newFilters.groupBy);
            if (newFilters.submissionStage !== submissionStage) setSubmissionStage(newFilters.submissionStage);
            if (newFilters.departmentId !== departmentId) setDepartmentId(newFilters.departmentId);
            onSharedFiltersChange({
              ...sharedFilters,
              dateRange: newFilters.dateRange,
              customDateRange: newFilters.customDateRange,
              dateType: newFilters.dateType,
              paymentType: newFilters.paymentType,
            });
          }}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <ClosedLostSavedViews filters={filters} onApply={handleApplyView} />
          <ExportButton label="Export CSV" variant="outlined" onClick={handleExport} isLoading={isExportInProgress} />
        </Stack>
      </Stack>
      <DateRangeBanner
        dateRange={filters.dateRange}
        customDateRange={filters.customDateRange}
        dateType={filters.dateType}
      />
      {loading || departmentOptionsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <ClosedLostKpis totals={totals} />
          <ClosedLostTable
            rows={rows}
            totals={totals}
            reasonTotals={reasonTotals}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
          />
          {departmentOptions.find(opt => String(opt.value) === departmentId)?.label && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Loss reasons reflect the {departmentOptions.find(opt => String(opt.value) === departmentId)?.label}{' '}
              department&apos;s configured reason set. Switch department to see a different reason taxonomy.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};
